export type LanguageCode =
    // sv is not a language code, but we treat it like one for legacy transifex reasons
    | 'sv'
    | 'de_AT'
    | 'de_CH'
    | 'de_DE'
    | 'en_AU'
    | 'en_CA'
    | 'en_DK'
    | 'en_FI'
    | 'en_GB'
    | 'en_HK'
    | 'en_IE'
    | 'en_IN'
    | 'en_NZ'
    | 'en_SE'
    | 'en_SG'
    | 'en_US'
    | 'es_AR'
    | 'es_CL'
    | 'es_CO'
    | 'es_ES'
    | 'es_MX'
    | 'es_PE'
    | 'fr_BE'
    | 'fr_CA'
    | 'fr_CH'
    | 'fr_FR'
    | 'it_IT'
    | 'nl_BE'
    | 'nl_NL'
    | 'nn_NO'
    | 'pt_BR'
    | 'pt_PT';

/**
 * Map of parent languages to child lanaguages in transifex.
 *
 * We use this both as a list of parent languages to pull down when pulling from transifex
 * and as a map to know what country codes to copy over when we register strings with i18next
 *
 * Parent -> child means children "inherit" from that parent code
 * instead of copying translations over
 *
 * undefined signifies a parent has no children
 */
export const languageInheritanceMap: Record<
    string,
    LanguageCode[] | undefined
> = {
    de_DE: ['de_AT', 'de_CH'],
    en_AU: ['en_NZ'],
    en_GB: ['en_IE', 'en_SG', 'en_HK', 'en_DK', 'en_FI'],
    en_US: ['en_CA'],
    es_AR: undefined,
    es_ES: ['es_CL', 'es_CO', 'es_PE'],
    es_MX: undefined,
    fr_FR: ['fr_BE', 'fr_CH'],
    fr_CA: undefined,
    it_IT: undefined,
    nl_NL: ['nl_BE'],
    pt_BR: undefined,
    pt_PT: undefined,
    // sv is different. Possibly explained by this verbatim comment by Patrick McLoughlin
    // My guess is that when we added Swedish (in 2010) for some reason Transifex didn’t support the full language code.
    // It does now.
    // It would be relatively straightforward for me to change codes in transifex but then you or someone would have to make sure the code is updated for core as well as eb-UI
    sv: undefined,
};

export const SUPPORTED_TLDS: { [tld: string]: LanguageCode } = Object.freeze({
    at: 'de_AT',
    be: 'nl_BE',
    ca: 'en_CA',
    ch: 'de_CH',
    cl: 'es_CL',
    co: 'es_CO',
    'co.nz': 'en_NZ',
    'co.uk': 'en_GB',
    com: 'en_US',
    'com.ar': 'es_AR',
    'com.au': 'en_AU',
    'com.br': 'pt_BR',
    'com.mx': 'es_MX',
    'com.pe': 'es_PE',
    de: 'de_DE',
    dk: 'en_DK',
    es: 'es_ES',
    fi: 'en_FI',
    fr: 'fr_FR',
    hk: 'en_HK',
    ie: 'en_IE',
    in: 'en_IN',
    it: 'it_IT',
    nl: 'nl_NL',
    no: 'nn_NO',
    pt: 'pt_PT',
    se: 'sv',
    sg: 'en_SG',
});
